import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircleHelp, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/scripts/AuthContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  MESSAGE_SEARCH_PLACEHOLDER,
  MESSAGE_SEARCH_SYNTAX,
} from "@/lib/messageSearchQuery";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 200;

/**
 * Messages sidebar omnibox — participant-scoped message search dropdown.
 */
export default function MessageSearchBar({ orgId }) {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const listboxId = useId();
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const reqGen = useRef(0);

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const hits = result?.hits || [];

  const runSearch = useCallback(
    async (q) => {
      if (!orgId || !accessToken) return;
      const trimmed = String(q || "").trim();
      if (!trimmed) {
        setResult(null);
        setLoading(false);
        return;
      }
      const gen = ++reqGen.current;
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/messages/search`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { q: trimmed, limit: 20 },
          }
        );
        if (gen !== reqGen.current) return;
        setResult(res.data || null);
        setActiveIndex(-1);
      } catch {
        if (gen !== reqGen.current) return;
        setResult(null);
      } finally {
        if (gen === reqGen.current) setLoading(false);
      }
    },
    [orgId, accessToken]
  );

  useEffect(() => {
    if (!orgId) {
      setQuery("");
      setResult(null);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      setResult(null);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, orgId, runSearch]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const goHit = useCallback(
    (hit) => {
      if (!hit?.href) return;
      setOpen(false);
      setQuery("");
      setResult(null);
      navigate(hit.href);
    },
    [navigate]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    if (activeIndex >= 0 && hits[activeIndex]) {
      goHit(hits[activeIndex]);
      return;
    }
    const exact = result?.exact_match;
    if (exact?.href) {
      goHit(exact);
      return;
    }
    if (hits.length === 1) {
      goHit(hits[0]);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp") && hits.length) {
      setOpen(true);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1 >= hits.length ? 0 : i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? hits.length - 1 : i - 1));
      return;
    }
    if (e.key === "Enter" && activeIndex >= 0 && hits[activeIndex]) {
      e.preventDefault();
      goHit(hits[activeIndex]);
    }
  };

  if (!orgId) return null;

  return (
    <div ref={wrapRef} className="relative w-full">
      <form onSubmit={onSubmit} className="flex w-full items-center gap-1">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={MESSAGE_SEARCH_PLACEHOLDER}
            className="h-8 pl-8 text-xs"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            autoComplete="off"
          />
        </div>
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-8 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label="Message search syntax help"
              >
                <CircleHelp className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="end"
              className="w-60 px-3 py-2.5"
            >
              <p className="mb-2 text-xs font-medium text-popover-foreground">
                Narrow message search
              </p>
              <ul className="space-y-1">
                {MESSAGE_SEARCH_SYNTAX.map((row) => (
                  <li
                    key={row.prefix}
                    className="flex items-baseline justify-between gap-3 text-xs"
                  >
                    <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-mono text-[11px] text-foreground">
                      {row.prefix}
                    </code>
                    <span className="min-w-0 text-right text-muted-foreground">
                      {row.meaning}
                    </span>
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </form>

      {open && query.trim() ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(50vh,20rem)] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
        >
          {loading && !result ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              Searching…
            </p>
          ) : null}
          {hits.map((hit, idx) => (
            <button
              key={hit.id}
              type="button"
              role="option"
              aria-selected={activeIndex === idx}
              className={cn(
                "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-xs hover:bg-accent",
                activeIndex === idx && "bg-accent"
              )}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => goHit(hit)}
            >
              <span className="font-medium truncate w-full">{hit.title}</span>
              {hit.subtitle ? (
                <span className="text-[11px] text-muted-foreground truncate w-full">
                  {hit.subtitle}
                </span>
              ) : null}
            </button>
          ))}
          {!loading && result && hits.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">
              No matching messages
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
